import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";
import { z } from "zod";
import {
  useBilling,
  useCancelBilling,
  useCheckout,
  useClientConfig,
  useDeleteFamily,
  useUpdateEmployer,
  useUser,
} from "@/api/queries";
import { PageHead } from "@/app/pages/PlanPage";
import { formatDate, fromJsDate } from "@/plan-logic/lib/dates";
import { authErrorMessage, useAuth } from "@/auth/AuthProvider";
import { errorText } from "@/components/ErrorPanel";
import { useToast } from "@/components/Toast";
import { ProductKeyState } from "@/generated/backend/v1/user";
import { BankConnectionsCard } from "@/flinks/BankConnectionsCard";
import "./settings.css";

const KEY_STATE: Record<ProductKeyState, string> = {
  [ProductKeyState.UNSPECIFIED]: "No plan code",
  [ProductKeyState.UNREGISTERED]: "No plan code",
  [ProductKeyState.ACTIVE]: "Active",
  [ProductKeyState.EXPIRED]: "Expired",
};

const passwordSchema = z
  .object({
    current: z.string().min(1, "Enter your current password."),
    next: z.string().min(8, "Choose a password with at least 8 characters."),
    confirm: z.string(),
  })
  .refine((v) => v.next === v.confirm, {
    message: "The two passwords don't match.",
    path: ["confirm"],
  });

const employerSchema = z
  .object({
    productKey: z.string().trim(),
    companyName: z.string().trim(),
  })
  .refine((v) => v.productKey || v.companyName, {
    message: "Enter your employer, a product key, or both.",
    path: ["companyName"],
  });

function Err({ message }: { message?: string }) {
  return message ? (
    <span className="err" role="alert">
      {message}
    </span>
  ) : null;
}

/** Firebase codes for a wrong current password (sign-in words them as an unknown account). */
const WRONG_PASSWORD = new Set([
  "auth/invalid-credential",
  "auth/wrong-password",
  "auth/invalid-login-credentials",
]);

function PasswordCard() {
  const { changePassword } = useAuth();
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { current: "", next: "", confirm: "" },
  });
  const onSubmit = handleSubmit(async ({ current, next }) => {
    setFormError(null);
    try {
      await changePassword(current, next);
      reset();
      toast("Password changed.");
    } catch (error) {
      const code = (error as { code?: string })?.code ?? "";
      setFormError(
        WRONG_PASSWORD.has(code) ? "That isn't your current password." : authErrorMessage(error),
      );
    }
  });
  return (
    <form
      className="card pad"
      id="password"
      onSubmit={onSubmit}
      noValidate
      data-testid="settings-password"
    >
      <h2 style={{ marginBottom: 14 }}>Change your password</h2>
      <div className="grid3">
        <label className="wp-field">
          Current password
          <input
            type="password"
            autoComplete="current-password"
            {...register("current")}
            data-testid="password-current"
          />
          <Err message={errors.current?.message} />
        </label>
        <label className="wp-field">
          New password
          <input
            type="password"
            autoComplete="new-password"
            {...register("next")}
            data-testid="password-new"
          />
          <Err message={errors.next?.message} />
        </label>
        <label className="wp-field">
          New password again
          <input
            type="password"
            autoComplete="new-password"
            {...register("confirm")}
            data-testid="password-confirm"
          />
          <Err message={errors.confirm?.message} />
        </label>
      </div>
      {formError && (
        <p className="wp-form-err" role="alert">
          {formError}
        </p>
      )}
      <button type="submit" className="wp-btn" disabled={isSubmitting} data-testid="password-save">
        {isSubmitting ? "Changing…" : "Change password"}
      </button>
    </form>
  );
}

function EmployerCard() {
  const { data: user } = useUser();
  const update = useUpdateEmployer();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof employerSchema>>({
    resolver: zodResolver(employerSchema),
    defaultValues: { productKey: "", companyName: "" },
  });
  const onSubmit = handleSubmit((values) =>
    update.mutate(values, {
      onSuccess: () => {
        reset();
        toast("Saved.");
      },
    }),
  );
  const state = user?.productKeyState ?? ProductKeyState.UNSPECIFIED;
  return (
    <form
      className="card pad"
      id="plan"
      onSubmit={onSubmit}
      noValidate
      data-testid="settings-employer"
    >
      <h2 style={{ marginBottom: 4 }}>Employer code or product key</h2>
      <p className="mut" style={{ marginBottom: 14, maxWidth: "62ch" }}>
        Optional. If your employer offers WealthyPlanet as a benefit, enter the code they gave you.
      </p>
      <dl className="wp-kv" data-testid="employer-current">
        <div>
          <dt>Employer</dt>
          <dd>{user?.companyName || "None"}</dd>
        </div>
        <div>
          <dt>Plan code</dt>
          <dd>{KEY_STATE[state] ?? "No plan code"}</dd>
        </div>
      </dl>
      <div className="grid2">
        <label className="wp-field">
          Employer
          <input {...register("companyName")} data-testid="employer-company" />
          <Err message={errors.companyName?.message} />
        </label>
        <label className="wp-field">
          Product key
          <input
            {...register("productKey")}
            autoComplete="off"
            spellCheck={false}
            data-testid="employer-key"
          />
        </label>
      </div>
      {update.isError && (
        <p className="wp-form-err" role="alert">
          {errorText(update.error)}
        </p>
      )}
      <button
        type="submit"
        className="wp-btn"
        disabled={update.isPending}
        data-testid="employer-save"
      >
        {update.isPending ? "Saving…" : "Save"}
      </button>
    </form>
  );
}

function UpgradeCard() {
  const { data: config } = useClientConfig();
  const checkout = useCheckout();
  if (!config?.stripeEnabled) return null;
  return (
    <div className="card pad" data-testid="settings-upgrade">
      <h2 style={{ marginBottom: 4 }}>Upgrade</h2>
      <p className="mut" style={{ marginBottom: 14, maxWidth: "62ch" }}>
        Unlock everything WealthyPlanet does. You&rsquo;ll finish on Stripe&rsquo;s secure checkout.
      </p>
      {checkout.isError && (
        <p className="wp-form-err" role="alert">
          {errorText(checkout.error)}
        </p>
      )}
      <button
        type="button"
        className="wp-btn"
        onClick={() => checkout.mutate()}
        disabled={checkout.isPending}
        data-testid="upgrade"
      >
        {checkout.isPending ? "Opening checkout…" : "Upgrade"}
      </button>
    </div>
  );
}

/** Stop recurring billing. The account keeps everything until the paid-for period ends. */
function CancelBillingCard() {
  const { data: config } = useClientConfig();
  const { data: billing } = useBilling(!!config?.stripeEnabled);
  const cancel = useCancelBilling();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  if (!config?.stripeEnabled || !billing) return null;
  const until = formatDate(fromJsDate(new Date(billing.currentPeriodEnd)));
  return (
    <div className="card pad" data-testid="settings-cancel-billing">
      <h2 style={{ marginBottom: 4 }}>Cancel billing</h2>
      {billing.cancelAtPeriodEnd ? (
        <p className="mut" style={{ maxWidth: "62ch" }} data-testid="billing-cancelled">
          Your billing is cancelled, so you won&rsquo;t be charged again. You keep everything
          you&rsquo;ve paid for until {until}.
        </p>
      ) : (
        <>
          <p className="mut" style={{ marginBottom: 14, maxWidth: "62ch" }}>
            Stop your payments renewing. Nothing changes until {until}, the end of the period
            you&rsquo;ve paid for; then your account goes back to the free version. Your household
            and plan stay.
          </p>
          {cancel.isError && (
            <p className="wp-form-err" role="alert">
              {errorText(cancel.error)}
            </p>
          )}
          {!open ? (
            <button
              type="button"
              className="wp-btn g"
              onClick={() => setOpen(true)}
              data-testid="cancel-billing-open"
            >
              Cancel billing
            </button>
          ) : (
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                className="wp-btn wp-btn-danger"
                disabled={cancel.isPending}
                onClick={() =>
                  cancel.mutate(undefined, {
                    onSuccess: () => {
                      setOpen(false);
                      toast("Billing cancelled.");
                    },
                  })
                }
                data-testid="cancel-billing-submit"
              >
                {cancel.isPending ? "Cancelling…" : "Yes, cancel billing"}
              </button>
              <button type="button" className="wp-btn g" onClick={() => setOpen(false)}>
                Keep billing
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const CONFIRM_WORD = "DELETE";

function DeleteCard() {
  const del = useDeleteFamily();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  return (
    <div className="card pad wp-danger" data-testid="settings-delete">
      <h2 style={{ marginBottom: 4 }}>Delete all your household data</h2>
      <p className="mut" style={{ marginBottom: 14, maxWidth: "62ch" }}>
        This removes your household, your plan and your saved what-ifs. Your login stays, so you can
        start again. It can&rsquo;t be undone.
      </p>
      {!open ? (
        <button
          type="button"
          className="wp-btn g"
          onClick={() => setOpen(true)}
          data-testid="delete-open"
        >
          Delete all my household data
        </button>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (typed !== CONFIRM_WORD) return;
            del.mutate(undefined, { onSuccess: () => navigate("/start", { replace: true }) });
          }}
        >
          <label className="wp-field">
            Type {CONFIRM_WORD} to confirm
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              data-testid="delete-confirm"
            />
          </label>
          {del.isError && (
            <p className="wp-form-err" role="alert">
              {errorText(del.error)}
            </p>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="submit"
              className="wp-btn wp-btn-danger"
              disabled={typed !== CONFIRM_WORD || del.isPending}
              data-testid="delete-submit"
            >
              {del.isPending ? "Deleting…" : "Delete everything"}
            </button>
            <button
              type="button"
              className="wp-btn g"
              onClick={() => {
                setOpen(false);
                setTyped("");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Settings (minimal): sign-in, connected banks, employer code, upgrade, cancel billing, delete all
 * household data. Needs no plan.
 */
export function SettingsPage() {
  const { user } = useAuth();
  const { data: appUser } = useUser();
  // The profile menu's "Password and login" and "Plan and billing" land on their cards (#password,
  // #plan), once the shell's scroll to the top of a new page has run.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const f = requestAnimationFrame(() =>
      document.getElementById(hash.slice(1))?.scrollIntoView?.({ block: "start" }),
    );
    return () => cancelAnimationFrame(f);
  }, [hash]);
  return (
    <section className="page on wp-settings" data-testid="page-settings">
      <PageHead title="pages.settings.title" sub="pages.settings.sub" />
      <div className="card pad" data-testid="settings-account">
        <h2 style={{ marginBottom: 6 }}>Your login</h2>
        <p style={{ fontSize: 15 }} data-testid="settings-email">
          {appUser?.email || user?.email}
        </p>
      </div>
      <PasswordCard />
      <BankConnectionsCard />
      <EmployerCard />
      <UpgradeCard />
      <CancelBillingCard />
      <DeleteCard />
    </section>
  );
}
